import isNull from 'lodash/isNull';
import type {
	EpicCardType,
	EstimationInfo,
} from '@atlassian/jira-backlog-epics-store/src/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	FullEpicDetails_boardScope_parents_edges_node as FullEpic,
	FullEpicDetails_boardScope_parents_edges_node_estimationInfo as Estimation,
} from './__generated_apollo__/FullEpicDetails.ts';

const isEpicLoadingBecauseOfOptimisticCreation = (epicId: string | number): boolean =>
	Number(epicId) < 0;

export const transformEstimationInfo = (estimation: Estimation): EstimationInfo | null => {
	const {
		details: { estimationType, name },
		childrenEstimationStats: { doneEstimate, totalEstimate, percentageCompleted, notEstimated },
	} = estimation;

	if (
		isNull(estimationType) ||
		isNull(name) ||
		isNull(doneEstimate) ||
		isNull(totalEstimate) ||
		isNull(percentageCompleted) ||
		isNull(notEstimated)
	) {
		return null;
	}

	return {
		estimationType,
		name,
		doneEstimate,
		totalEstimate,
		percentageCompleted: percentageCompleted / 100,
		notEstimated,
	};
};

export const transformFullCard = ({
	id,
	key,
	summary,
	color,
	childrenInfo,
	estimationInfo,
	startDate,
	dueDate,
}: FullEpic): EpicCardType => {
	const epicData = {
		id: Number(id),
		key,
		name: summary,
		color: isNull(color) ? undefined : color,
		isLoading: fg('prefetch-epics-data-backlog')
			? undefined
			: isEpicLoadingBecauseOfOptimisticCreation(id),
		hasError: false,
		startDate: isNull(startDate) ? undefined : startDate,
		dueDate: isNull(dueDate) ? undefined : dueDate,
	};

	const { todoStats, inProgressStats, doneStats } = childrenInfo || {};
	const toDo = todoStats && !isNull(todoStats.issueCount) ? todoStats.issueCount : 0;
	const inProgress =
		inProgressStats && !isNull(inProgressStats.issueCount) ? inProgressStats.issueCount : 0;
	const done = doneStats && !isNull(doneStats.issueCount) ? doneStats.issueCount : 0;

	return {
		epic: epicData,
		progress: {
			status: {
				toDo: {
					issueCount: toDo,
				},
				inProgress: {
					issueCount: inProgress,
				},
				done: {
					issueCount: done,
				},
			},
			totalIssues: toDo + inProgress + done,
		},
		estimation: estimationInfo ? transformEstimationInfo(estimationInfo) : null,
	};
};
